import { DUPLICATE_REASON_LABELS, formatDateTime, formatPhone, fullName } from '@hr/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { CopyIcon, MergeIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { EmptyState, PageHeader } from '@/components/page';
import { Pagination } from '@/components/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { uk } from '@/i18n/uk';
import { useOverlays } from '@/lib/overlays';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { errorMessage, trpc } from '@/lib/trpc';
import { isHr } from '@/lib/roles';
import type { RouterOutputs } from '@/lib/types';

type Flag = RouterOutputs['duplicates']['list']['items'][number];

export const Route = createFileRoute('/_app/duplicates')({
  validateSearch: z.object({
    ...listSearchShape,
    status: z.enum(['open', 'dismissed']).optional().catch(undefined),
  }),
  beforeLoad: ({ context }) => {
    if (!isHr(context.me.role)) throw redirect({ to: '/' });
  },
  component: DuplicatesPage,
});

function DuplicatesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  const overlays = useOverlays();
  const { me } = Route.useRouteContext();
  const isAdmin = me.role === 'admin';
  const [merging, setMerging] = useState<Flag | null>(null);
  const status = search.status ?? 'open';
  const state = tableState(search, { sort: 'createdAt', dir: 'desc', size: 10 });
  const list = useQuery({
    ...trpc.duplicates.list.queryOptions({
      status,
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
    }),
    placeholderData: keepPreviousData,
  });
  const dismiss = useMutation(
    trpc.duplicates.dismiss.mutationOptions({
      onSuccess: () => toast.success(uk.duplicates.dismissed),
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.duplicates.pathFilter()),
    }),
  );

  return (
    <>
      <PageHeader
        title={uk.duplicates.title}
        description={!isAdmin && uk.merge.adminOnly}
        actions={
          <div className="flex rounded-md border p-0.5" role="group">
            {(['open', 'dismissed'] as const).map((s) => (
              <Button
                key={s}
                size="sm"
                variant={status === s ? 'secondary' : 'ghost'}
                aria-pressed={status === s}
                onClick={() => void navigate({ search: { status: s === 'open' ? undefined : s } })}
              >
                {s === 'open' ? uk.duplicates.open : uk.duplicates.dismissedTab}
              </Button>
            ))}
          </div>
        }
      />
      {list.isPending ? (
        <Skeleton className="h-40" />
      ) : list.data?.items.length === 0 ? (
        <EmptyState icon={CopyIcon} title={uk.duplicates.empty} hint={uk.duplicates.emptyHint} />
      ) : (
        <ul className="space-y-3">
          {list.data?.items.map((f) => {
            return (
              <li key={f.id} className="rounded-lg border p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  {f.reasons.map((reason) => (
                    <Badge
                      key={reason}
                      variant="outline"
                      className="border-amber-300 text-amber-800"
                    >
                      {DUPLICATE_REASON_LABELS[reason]}
                      {reason === 'similar_name' && ` · ${Math.round(f.score * 100)}%`}
                    </Badge>
                  ))}
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(f.createdAt)}
                  </span>
                  {status === 'open' && (
                    <div className="ml-auto flex gap-2">
                      {isAdmin && (
                        <Button size="sm" onClick={() => setMerging(f)}>
                          <MergeIcon /> {uk.merge.action}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={dismiss.isPending}
                        onClick={() => dismiss.mutate(f.id)}
                      >
                        {uk.duplicates.dismiss}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[f.a, f.b].map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => overlays.openCandidate(c.id)}
                      className="rounded-md border bg-muted/30 p-2.5 text-left text-sm hover:bg-muted"
                    >
                      <div className="font-medium">{fullName(c)}</div>
                      <div className="text-xs text-muted-foreground">
                        {[c.phoneE164 && formatPhone(c.phoneE164), c.email, c.city]
                          .filter(Boolean)
                          .join(' · ') || uk.common.empty}
                      </div>
                    </button>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {list.data && list.data.total > 0 && (
        <div className="mt-3">
          <Pagination
            total={list.data.total}
            state={state}
            onStateChange={(next) =>
              void navigate({
                search: (prev) => ({ ...prev, ...tableStateToSearch(next) }),
                replace: true,
              })
            }
          />
        </div>
      )}
      {merging && <MergeDialog flag={merging} onClose={() => setMerging(null)} />}
    </>
  );
}

function MergeDialog({ flag, onClose }: { flag: Flag; onClose: () => void }) {
  const qc = useQueryClient();
  const [keepId, setKeepId] = useState(
    // За замовчуванням лишаємо старший запис — у нього зазвичай довша історія.
    flag.a.createdAt <= flag.b.createdAt ? flag.a.id : flag.b.id,
  );
  const merge = useMutation(
    trpc.duplicates.merge.mutationOptions({
      onSuccess: () => {
        toast.success(uk.merge.done);
        onClose();
      },
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => {
        void qc.invalidateQueries(trpc.duplicates.pathFilter());
        void qc.invalidateQueries(trpc.candidates.pathFilter());
        void qc.invalidateQueries(trpc.applications.pathFilter());
      },
    }),
  );
  const removeId = keepId === flag.a.id ? flag.b.id : flag.a.id;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{uk.merge.title}</DialogTitle>
          <DialogDescription>{uk.merge.description}</DialogDescription>
        </DialogHeader>
        <RadioGroup value={keepId} onValueChange={setKeepId} className="grid gap-2 sm:grid-cols-2">
          {[flag.a, flag.b].map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer gap-3 rounded-md border p-3 text-sm has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
            >
              <RadioGroupItem value={c.id} className="mt-0.5" />
              <div className="min-w-0">
                <div className="font-medium">{fullName(c)}</div>
                <div className="text-xs text-muted-foreground">
                  {[c.phoneE164 && formatPhone(c.phoneE164), c.email, c.city]
                    .filter(Boolean)
                    .join(' · ') || uk.common.empty}
                </div>
                <div className="text-xs text-muted-foreground">{formatDateTime(c.createdAt)}</div>
                {c.id === keepId && (
                  <div className="mt-1 text-xs font-medium text-primary">{uk.merge.keep}</div>
                )}
              </div>
            </label>
          ))}
        </RadioGroup>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {uk.common.cancel}
          </Button>
          <Button disabled={merge.isPending} onClick={() => merge.mutate({ keepId, removeId })}>
            {merge.isPending ? uk.common.saving : uk.merge.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
